import type {
  AgencyDto,
  CreateAgencyRequestDto,
  GetAgenciesResponseDto,
  RemoveAgencyResponseDto,
  ReorderAgencyDivisionsRequestDto,
  UpdateAgencyRequestDto,
} from 'shared/dtos/agencies.dto';
import type { AppState } from 'store';

import { createAsyncThunk } from '@reduxjs/toolkit';
import AgenciesService from 'services/agencies.service';

const agenciesService = new AgenciesService();

export const getAgenciesThunk = createAsyncThunk<GetAgenciesResponseDto>(
  'agencies/getAgencies',
  async (_, thunkAPI) => {
    try {
      const result = await agenciesService.getAgencies();
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const reorderAgencyDivisionsThunk = createAsyncThunk<
  AgencyDto,
  ReorderAgencyDivisionsRequestDto & { agencyId: number }
>('agencies/reorderAgencyDivisions', async (payload, thunkAPI) => {
  try {
    const { agencyId, ...data } = payload;
    const result = await agenciesService.reorderAgencyDivisions(agencyId, data);
    return result;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const getAgenciesWithDivisionsThunk =
  createAsyncThunk<GetAgenciesResponseDto>(
    'agencies/getAgenciesWithDivisions',
    async (_, thunkAPI) => {
      try {
        const result = await agenciesService.getAgenciesWithDivisions();
        return result;
      } catch (error) {
        return thunkAPI.rejectWithValue(error);
      }
    },
  );

export const getAgencyThunk = createAsyncThunk<
  AgencyDto,
  number,
  { state: AppState }
>(
  'agencies/getAgency',
  async (id, thunkAPI) => {
    try {
      const result = await agenciesService.getAgency(id);
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (state.agencies.isGetAgencyThunkPending) {
        return false;
      }
      return true;
    },
  },
);

export const removeAgencyThunk = createAsyncThunk<
  RemoveAgencyResponseDto,
  number
>('agencies/removeAgency', async (id, thunkAPI) => {
  try {
    const result = await agenciesService.removeAgency(id);
    thunkAPI.dispatch(getAgenciesThunk());
    return result;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const createAgencyThunk = createAsyncThunk<
  AgencyDto,
  CreateAgencyRequestDto
>('agencies/createAgency', async (formData, thunkAPI) => {
  try {
    const result = await agenciesService.createAgency(formData);
    //REMOVE_AGENCY_FROM_STORE
    thunkAPI.dispatch(getAgenciesThunk());
    return result;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const updateAgencyThunk = createAsyncThunk<
  AgencyDto,
  UpdateAgencyRequestDto & { id: number },
  { state: AppState }
>(
  'agencies/updateAgency',
  async (payload, thunkAPI) => {
    try {
      const { id, ...formData } = payload;
      const result = await agenciesService.updateAgency(id, formData);
      thunkAPI.dispatch(getAgencyThunk(id));
      thunkAPI.dispatch(getAgenciesWithDivisionsThunk());
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (state.agencies.isUpdateAgencyThunkPending) {
        return false;
      }
      return true;
    },
  },
);
